import { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import {
    createManualContractGeneration,
    deleteEmployeeContract,
    getEmployeeContractFile,
    getEmployeeContractsPrintPackage,
    listEmployeeContracts,
    retryEmployeeContractGeneration,
    returnEmployeeToCandidateDocuments,
    uploadEmployeeContract,
} from "../services/employeeContractsApi";
import { CANDIDATE_DOCUMENT_LABELS, CANDIDATE_DOCUMENT_TYPES } from "../config/candidateDocumentTypes";
import { CONTRACT_TYPES as PRIMARY_CONTRACT_TYPES, contractTypeLabel } from "../config/contractTypes";
import { DEPARTMENTS } from "../config/api";
import { LEGAL_ENTITIES } from "../constants/employmentTerms";
import { parseCompensationInput } from "../utils/compensation";
import {
    MAX_CANDIDATE_DOCUMENT_FILES,
    validateCandidateDocumentFile,
    validateCandidateDocumentPackage,
} from "../utils/candidateDocumentsDraft";
import "./EmployeeContractsPage.css";

const CONTRACT_TYPES = {
    employment_contract: "Трудовой договор",
    employment_application: "Заявление о приёме на работу",
    personal_data_consent: "Согласие на обработку персональных данных",
    additional_agreement: "Дополнительное соглашение",
    nda: "Соглашение о конфиденциальности",
    liability_agreement: "Договор о материальной ответственности",
    bank_details_application: "Заявление с банковскими реквизитами",
    other: "Другой документ",
};

const CONTRACT_FILTERS = [
    { value: "all", label: "Все сотрудники" },
    { value: "ready", label: "Договоры готовы" },
    { value: "processing", label: "В обработке" },
    { value: "issues", label: "Есть проблема" },
];

const REQUIRED_MANUAL_DOCUMENTS = [
    "passport_personal_data", "passport_registration", "snils",
];
const SINGLETON_MANUAL_DOCUMENTS = new Set([
    "passport_personal_data", "passport_registration", "snils", "bank_details",
]);

function newManualDocuments() {
    return REQUIRED_MANUAL_DOCUMENTS.map(kind => ({ kind, file: null }));
}

function ManualGenerationForm({ onCreated, onCancel }) {
    const { showAlert } = useAlertContext();
    const [data, setData] = useState({
        full_name: "", department: "", position: "", date_hired: "",
        legal_entity: "", salary: "", work_schedule: "",
        phone_number: "", email: "", birth_date: "", actual_residence_address: "",
        contract_type: "employment",
    });
    const [documents, setDocuments] = useState(newManualDocuments);
    const [submitting, setSubmitting] = useState(false);
    const attachedDocuments = documents.filter(item => item.file);
    const attachedBytes = attachedDocuments.reduce((sum, item) => sum + Number(item.file?.size || 0), 0);
    const setField = event => setData(current => ({ ...current, [event.target.name]: event.target.value }));
    const setDocument = (index, patch) => setDocuments(current => current.map(
        (item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item,
    ));
    const submit = async event => {
        event.preventDefault();
        let checkedPackage;
        try {
            checkedPackage = validateCandidateDocumentPackage(attachedDocuments.map(item => item.file));
            const attachedKinds = new Set(attachedDocuments.map(item => item.kind));
            const missingKinds = REQUIRED_MANUAL_DOCUMENTS.filter(kind => !attachedKinds.has(kind));
            if (missingKinds.length) {
                showAlert(`Приложите обязательные документы: ${missingKinds.map(kind => CANDIDATE_DOCUMENT_LABELS[kind] || kind).join(", ")}`, "error");
                return;
            }
            if (!parseCompensationInput(data.salary)) {
                showAlert("Укажите положительную сумму или условия оплаты", "error");
                return;
            }
        } catch (error) {
            showAlert(error.message, "error");
            return;
        }
        setSubmitting(true);
        try {
            const result = await createManualContractGeneration(data, attachedDocuments);
            if (result.status === "MissingDocuments" || !result.queued) {
                showAlert("Пакет создан, но для генерации не хватает ключевых документов", "warning");
            } else {
                showAlert(`Пакет создан и поставлен в очередь: ${result.queued} файл(а)`, "success");
            }
            await onCreated();
        } catch (error) {
            const message = String(error?.message || "");
            showAlert(
                message.includes("Нет связи")
                    ? `Не удалось передать пакет (${formatSize(checkedPackage.totalBytes)}). Проверьте интернет или VPN и повторите отправку — выбранные файлы сохранены в форме`
                    : message || "Не удалось отправить документы на генерацию",
                "error",
            );
        } finally {
            setSubmitting(false);
        }
    };
    return <section className="manual-generation-panel" aria-label="Ручная отправка документов на генерацию">
        <div className="manual-generation-intro"><div><span>НОВЫЙ ПАКЕТ</span><h2>Отправить документы вручную</h2><p>Для человека, которого ещё нет на платформе. Карточка оформления создастся автоматически, а документы сразу уйдут на составление договоров.</p></div><button type="button" disabled={submitting} onClick={onCancel} aria-label="Закрыть ручную отправку">×</button></div>
        <form onSubmit={submit}>
            <fieldset><legend>Данные кандидата</legend><div className="manual-generation-fields">
                <label><span>Тип договора *</span><select name="contract_type" value={data.contract_type} onChange={setField}>{PRIMARY_CONTRACT_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
                <label><span>ФИО *</span><input name="full_name" value={data.full_name} onChange={setField} required maxLength="300" placeholder="Иванов Иван Иванович" /></label>
                <label><span>Подразделение *</span><select name="department" value={data.department} onChange={setField} required data-testid="manual-contract-department"><option value="" disabled>Выберите подразделение</option>{DEPARTMENTS.map(department => <option key={department} value={department}>{department}</option>)}</select></label>
                <label><span>Должность *</span><input name="position" value={data.position} onChange={setField} required maxLength="300" /></label>
                <label><span>Дата выхода *</span><input name="date_hired" type="date" value={data.date_hired} onChange={setField} required /></label>
                <label><span>Юридическое лицо *</span><select name="legal_entity" value={data.legal_entity} onChange={setField} required><option value="" disabled>Выберите юридическое лицо</option>{LEGAL_ENTITIES.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
                <label><span>Оплата: сумма или условия *</span><input name="salary" type="text" maxLength={1000} value={data.salary} onChange={setField} required placeholder="100000 или 10–25% от комиссии" /></label>
                <label><span>График работы *</span><input name="work_schedule" value={data.work_schedule} onChange={setField} required maxLength="200" placeholder="Например, 5/2 с 09:00 до 18:00" /></label>
                <label><span>Телефон</span><input name="phone_number" value={data.phone_number} onChange={setField} maxLength="20" /></label>
                <label><span>Email</span><input name="email" type="email" value={data.email} onChange={setField} maxLength="254" /></label>
                <label><span>Дата рождения</span><input name="birth_date" type="date" value={data.birth_date} onChange={setField} /></label>
                <label className="manual-generation-address"><span>Фактический адрес проживания *</span><textarea name="actual_residence_address" value={data.actual_residence_address} onChange={setField} required maxLength="2000" rows="3" placeholder="Индекс, регион, город, улица, дом, квартира" /></label>
            </div></fieldset>
            <fieldset><div className="manual-generation-files-heading"><legend>Исходные документы</legend><button type="button" disabled={documents.length >= MAX_CANDIDATE_DOCUMENT_FILES} onClick={() => setDocuments(current => [...current, { kind: "other", file: null }])}>+ Добавить файл</button></div><p>Для генерации обязательно загрузите разворот и регистрацию паспорта, а также СНИЛС. Банковские реквизиты можно добавить при наличии — без них банковские поля останутся пустыми для ручного заполнения. Каждый приложенный файл проходит проверку. Один файл — до 10 МБ, весь пакет — до 50 МБ.</p>
                <div className="manual-generation-files">{documents.map((item, index) => <div key={`${index}-${item.kind}`} className="manual-generation-file-row">
                    <select aria-label={`Тип исходного документа ${index + 1}`} value={item.kind} onChange={event => setDocument(index, { kind: event.target.value })}>{CANDIDATE_DOCUMENT_TYPES.map(type => <option key={type.value} value={type.value} disabled={SINGLETON_MANUAL_DOCUMENTS.has(type.value) && documents.some((other, otherIndex) => otherIndex !== index && other.kind === type.value)}>{type.label}</option>)}</select>
                    <label className={item.file ? "has-file" : ""}><input aria-label={`Файл исходного документа ${index + 1}`} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={event => {
                        const selected = event.target.files?.[0] || null;
                        if (!selected) return setDocument(index, { file: null });
                        try {
                            validateCandidateDocumentFile(selected);
                            setDocument(index, { file: selected });
                        } catch (error) {
                            event.target.value = "";
                            setDocument(index, { file: null });
                            showAlert(error.message, "error");
                        }
                    }} /><span>{item.file?.name || "Выбрать файл"}</span></label>
                    {documents.length > 1 ? <button type="button" aria-label={`Удалить строку документа ${index + 1}`} onClick={() => setDocuments(current => current.filter((_, itemIndex) => itemIndex !== index))}>×</button> : null}
                </div>)}</div>
                <p className="manual-generation-size" aria-live="polite">Выбрано: {attachedDocuments.length} файл(а), {formatSize(attachedBytes)} из 50 МБ</p>
            </fieldset>
            <div className="manual-generation-actions"><button type="button" disabled={submitting} onClick={onCancel}>Отмена</button><button type="submit" disabled={submitting}>{submitting ? "Отправляем…" : "Создать и отправить на генерацию"}</button></div>
        </form>
    </section>;
}

function employeeMatchesFilter(employee, filter) {
    if (filter === "issues") return ["Dead", "MissingDocuments", "CompletedWithWarnings"].includes(employee.contract_generation_status);
    if (filter === "processing") return ["Queued", "Sent"].includes(employee.contract_generation_status);
    if (filter === "ready") return ["Completed", "CompletedWithWarnings"].includes(employee.contract_generation_status) || (
        !["Dead", "MissingDocuments", "Queued", "Sent"].includes(employee.contract_generation_status)
        && employee.documents?.length > 0
    );
    return true;
}

function generationPresentation(status) {
    if (status === "Dead") return { tone: "danger", label: "Ошибка генерации", hint: "Проверьте исходные документы и повторите отправку" };
    if (status === "MissingDocuments") return { tone: "warning", label: "Не хватает документов", hint: "Верните пакет на дополнение" };
    if (["Queued", "Sent"].includes(status)) return { tone: "info", label: "Договоры формируются", hint: "Результат появится автоматически" };
    if (status === "CompletedWithWarnings") return { tone: "warning", label: "Сформированы с замечаниями", hint: "Часть полей нужно проверить и заполнить вручную" };
    if (status === "Completed") return { tone: "success", label: "Договоры сформированы", hint: "Пакет готов к просмотру и печати" };
    return { tone: "neutral", label: "Ручная работа", hint: "Можно загрузить готовые документы" };
}

function missingDocumentLabels(documentTypes) {
    if (!Array.isArray(documentTypes)) return [];
    return [...new Set(documentTypes.map(type => String(type || "").trim()).filter(Boolean))]
        .map(type => CANDIDATE_DOCUMENT_LABELS[type] || "Другой обязательный документ");
}

function formatSize(bytes) {
    if (!Number.isFinite(Number(bytes))) return "";
    return `${(Number(bytes) / 1024 / 1024).toFixed(1)} МБ`;
}

export async function openEmployeeContract(document, { print = false } = {}) {
    const isDocx = String(document.content_type || "").includes("wordprocessingml")
        || String(document.filename || "").toLowerCase().endsWith(".docx");
    if (isDocx) {
        const blob = await getEmployeeContractFile(document.id);
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = url;
        link.download = document.filename || "Документ.docx";
        link.style.display = "none";
        window.document.body.appendChild(link);
        try {
            link.click();
        } finally {
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
        return true;
    }
    const popup = window.open("", "_blank");
    if (!popup) throw new Error("Браузер заблокировал окно документа. Разрешите всплывающие окна для платформы");
    try {
        popup.opener = null;
        const blob = await getEmployeeContractFile(document.id);
        const url = URL.createObjectURL(blob);
        const revoke = () => window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        if (popup.location?.replace) popup.location.replace(url);
        else popup.location = url;
        if (print) {
            popup.addEventListener("load", () => {
                try { popup.print(); } finally { revoke(); }
            }, { once: true });
        } else {
            revoke();
        }
        return true;
    } catch (error) {
        try { popup.close(); } catch (_) { /* already closed */ }
        throw error;
    }
}

export async function openContractsPrintPackage(employeeId) {
    const popup = window.open("", "_blank");
    if (!popup) throw new Error("Браузер заблокировал окно печати. Разрешите всплывающие окна для платформы");
    try {
        popup.opener = null;
        const blob = await getEmployeeContractsPrintPackage(employeeId);
        const url = URL.createObjectURL(blob);
        const revoke = () => window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        try {
            if (popup.location?.replace) popup.location.replace(url);
            else popup.location = url;
            popup.addEventListener("load", () => {
                try { popup.print(); } finally { revoke(); }
            }, { once: true });
        } catch (_) {
            popup.location = url;
            revoke();
        }
        return true;
    } catch (error) {
        try { popup.close(); } catch (_) { /* already closed */ }
        throw error;
    }
}

function ContractCard({ document, onDelete, onOpen, opening }) {
    const isImage = String(document.content_type || "").startsWith("image/");
    const isDocx = String(document.content_type || "").includes("wordprocessingml");
    const formatLabel = isImage ? "IMG" : isDocx ? "DOCX" : "PDF";
    return <article className="contract-document-card">
        <button type="button" className="contract-preview" aria-label={`Просмотреть ${document.filename}`} disabled={opening} onClick={() => onOpen(document)}>
            {isImage
                ? <img src={document.url} alt={CONTRACT_TYPES[document.document_type] || document.filename} />
                : <span className={`contract-format-icon is-${formatLabel.toLowerCase()}`} aria-hidden="true"><small>ФАЙЛ</small><b>{formatLabel}</b></span>}
        </button>
        <div className="contract-document-body">
            <strong>{CONTRACT_TYPES[document.document_type] || document.document_type}</strong>
            <span title={document.filename}>{document.filename}</span>
            <small>{formatSize(document.size)} · {new Date(document.created_at).toLocaleDateString("ru-RU")}</small>
            <div className="contract-actions">
                <button type="button" disabled={opening} onClick={() => onOpen(document)}>{opening ? "Открываем…" : isDocx ? "Открыть DOCX" : "Просмотр"}</button>
                <button type="button" disabled={opening} onClick={() => onOpen(document, true)}>{isDocx ? "Открыть для печати" : "Печать"}</button>
                <button type="button" className="is-danger" aria-label={`Удалить ${document.filename}`} onClick={() => onDelete(document)}>×</button>
            </div>
        </div>
    </article>;
}

function EmployeeContracts({ employee, onChanged }) {
    const { showAlert } = useAlertContext();
    const [type, setType] = useState("employment_contract");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [returning, setReturning] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [selectedContractType, setSelectedContractType] = useState(
        employee.contract_type || "employment",
    );
    const [printing, setPrinting] = useState(false);
    const [openingDocumentId, setOpeningDocumentId] = useState(null);

    useEffect(() => {
        setSelectedContractType(employee.contract_type || "employment");
    }, [employee.contract_type]);

    const upload = async event => {
        event.preventDefault();
        if (!file) return;
        const form = event.currentTarget;
        setUploading(true);
        try {
            await uploadEmployeeContract(employee.id, type, file);
            setFile(null);
            form.reset();
            await onChanged();
            showAlert("Договор загружен", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить договор", "error");
        } finally {
            setUploading(false);
        }
    };

    const remove = async document => {
        if (!window.confirm(`Удалить «${document.filename}»?`)) return;
        try {
            await deleteEmployeeContract(document.id);
            await onChanged();
            showAlert("Договор удалён", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось удалить договор", "error");
        }
    };

    const returnToCandidateDocuments = async () => {
        if (!window.confirm(`Вернуть пакет документов сотрудника «${employee.full_name}» на корректировку? Сотрудник исчезнет из раздела договоров до повторного подтверждения полного пакета.`)) return;
        setReturning(true);
        try {
            await returnEmployeeToCandidateDocuments(employee.id);
            await onChanged();
            showAlert("Сотрудник возвращён в документы кандидатов", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось вернуть пакет на корректировку", "error");
        } finally {
            setReturning(false);
        }
    };

    const retryGeneration = async (allowIncomplete = false, nextContractType = null) => {
        const replacingReadyFiles = ["Completed", "CompletedWithWarnings"].includes(employee.contract_generation_status);
        const changingType = nextContractType && nextContractType !== employee.contract_type;
        const missingWarning = allowIncomplete
            ? ` Не хватает документов: ${missingDocumentLabels(employee.contract_generation_missing_document_types).join(", ")}. Незаданные реквизиты останутся пустыми.`
            : "";
        const question = changingType
            ? `Переоформить пакет «${employee.full_name}»: ${contractTypeLabel(employee.contract_type)} → ${contractTypeLabel(nextContractType)}? Будут использованы уже загруженные исходные документы.${missingWarning}${replacingReadyFiles ? " Текущие сформированные системой файлы будут заменены; сохраните свои правки перед продолжением." : ""}`
            : allowIncomplete
            ? `Не хватает документов: ${missingDocumentLabels(employee.contract_generation_missing_document_types).join(", ")}. Сформировать договоры без них? Незаданные реквизиты останутся пустыми и потребуют ручной проверки.`
            : `Повторно отправить документы сотрудника «${employee.full_name}» на генерацию договоров?${replacingReadyFiles ? " Сформированные системой файлы будут заменены; сохраните свои правки перед повтором." : ""}`;
        if (!window.confirm(question)) return;
        setRetrying(true);
        try {
            const result = await retryEmployeeContractGeneration(employee.id, {
                allow_incomplete: allowIncomplete,
                ...(nextContractType ? { contract_type: nextContractType } : {}),
            });
            await onChanged();
            showAlert(
                changingType
                    ? `Тип договора изменён на «${contractTypeLabel(result.contract_type || nextContractType)}». Пакет отправлен на формирование`
                    : `Документы повторно отправлены: ${result.requeued}`,
                "success",
            );
        } catch (error) {
            showAlert(error.message || "Не удалось повторно отправить документы", "error");
        } finally {
            setRetrying(false);
        }
    };
    const printPackage = async () => {
        setPrinting(true);
        try {
            await openContractsPrintPackage(employee.id);
            showAlert("Печатный пакет подготовлен", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось подготовить договоры к печати", "error");
        } finally {
            setPrinting(false);
        }
    };
    const openDocument = async (document, print = false) => {
        setOpeningDocumentId(document.id);
        try {
            await openEmployeeContract(document, { print });
        } catch (error) {
            showAlert(error.message || "Не удалось открыть документ. Обновите страницу и повторите попытку", "error");
        } finally {
            setOpeningDocumentId(null);
        }
    };
    const generation = generationPresentation(employee.contract_generation_status);
    const missingLabels = missingDocumentLabels(
        employee.contract_generation_missing_document_types,
    );

    return <section className="employee-contract-section">
        <header className="employee-contract-header">
            <div className="employee-contract-person"><div className="employee-contract-avatar">{String(employee.full_name).split(/\s+/).slice(0, 2).map(part => part[0]).join("")}</div><div><div className="employee-contract-title"><h2>{employee.full_name}</h2><span className={`contract-status-pill is-${generation.tone}`}>{generation.label}</span></div><p>{employee.position} · {employee.department}</p><small>{contractTypeLabel(employee.contract_type)} · Дата выхода: {new Date(employee.date_hired).toLocaleDateString("ru-RU")}{employee.phone_number ? ` · ${employee.phone_number}` : ""}</small></div></div>
            <div className="employee-contract-summary"><div><strong>{employee.documents.length}</strong><span>договоров</span></div><div><strong>{employee.source_documents?.length || 0}</strong><span>исходников</span></div></div>
        </header>
        <section className="contract-reissue-panel" aria-label={`Переоформление договора для ${employee.full_name}`}>
            <div><strong>Переоформить договор на другой тип</strong><p>Повторно загружать документы не нужно — система использует исходники из этой карточки и сформирует новый комплект.</p></div>
            <label><span>Новый тип договора</span><select aria-label={`Новый тип договора для ${employee.full_name}`} value={selectedContractType} onChange={event => setSelectedContractType(event.target.value)}>{PRIMARY_CONTRACT_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <button type="button" disabled={retrying || !employee.source_documents?.length || selectedContractType === (employee.contract_type || "employment")} onClick={() => retryGeneration(Boolean(employee.contract_generation_missing_document_types?.length), selectedContractType)}>{retrying ? "Отправляем…" : "Сменить тип и сформировать"}</button>
        </section>
        {employee.contract_generation_status === "Dead" ? <div className="contract-generation-error" role="alert">
            <span aria-hidden="true">!</span><div><strong>Договоры не сформированы</strong><p>{employee.contract_generation_error || "Не удалось обработать документы. Проверьте исходные файлы и повторите отправку."}</p></div>
            <button type="button" disabled={retrying} onClick={() => retryGeneration(false)}>{retrying ? "Отправляем…" : "Повторить генерацию"}</button>
        </div> : null}
        {employee.contract_generation_status === "MissingDocuments" ? <div className="contract-generation-missing" role="alert">
            <span aria-hidden="true">!</span><div><strong>Для составления договоров не хватает:</strong>
                {missingLabels.length
                    ? <ul>{missingLabels.map((label, index) => <li key={`${label}-${index}`}>{label}</li>)}</ul>
                    : <p>Система не получила один или несколько обязательных документов.</p>}
                <small>Загружено исходных файлов: {employee.source_documents?.length || 0}. Полнота пакета зависит от типов документов, а не от их количества.</small>
                <p>Можно отправить имеющиеся файлы на генерацию: отсутствующие реквизиты останутся пустыми, готовые документы нужно проверить и дополнить вручную.</p>
            </div>
            <button type="button" disabled={retrying || !employee.source_documents?.length} onClick={() => retryGeneration(true)}>{retrying ? "Отправляем…" : "Сформировать без недостающих документов"}</button>
        </div> : null}
        {employee.contract_generation_status === "CompletedWithWarnings" ? <div className="contract-generation-missing" role="alert">
            <span aria-hidden="true">!</span><div><strong>Договоры сформированы, но часть данных не распознана</strong><p>{employee.contract_generation_error || "Проверьте пустые поля в готовых документах и заполните их вручную."}</p><small>Готовые файлы доступны для просмотра, редактирования и печати.</small></div>
            <button type="button" disabled={retrying || !employee.source_documents?.length} onClick={() => retryGeneration(false)}>{retrying ? "Отправляем…" : "Повторно сформировать"}</button>
        </div> : null}
        {["Queued", "Sent"].includes(employee.contract_generation_status) ? <div className="contract-generation-pending"><span aria-hidden="true"/><div><strong>{generation.label}</strong><p>{generation.hint}</p></div></div> : null}
        <details className="source-documents" open={["Dead", "MissingDocuments", "CompletedWithWarnings"].includes(employee.contract_generation_status)}>
            <summary><span><strong>Исходные документы кандидата</strong><small>Паспорт, СНИЛС и другие файлы, на основе которых составляются договоры</small></span><b>{employee.source_documents?.length || 0}</b></summary>
            {employee.source_documents?.length ? <div className="source-documents-grid">{employee.source_documents.map(document => <a key={document.id} href={document.url} target="_blank" rel="noreferrer">
                <span className="source-document-icon" aria-hidden="true">{String(document.filename || "FILE").split(".").pop().slice(0, 4).toUpperCase()}</span>
                <span><strong>{CANDIDATE_DOCUMENT_LABELS[document.kind] || document.kind}</strong><small>{document.filename}</small></span>
                <b>Открыть ↗</b>
            </a>)}</div> : <p>Исходные документы не найдены.</p>}
        </details>
        <section className="employee-contract-documents">
            <div className="contract-section-heading"><div><span>ГОТОВЫЕ ФАЙЛЫ</span><h3>Договоры и заявления</h3><p>Открывайте документы для проверки, печати или загрузите готовый файл вручную.</p></div><div className="contract-heading-actions"><span className="contract-count">{employee.documents.length} док.</span>{employee.contract_generation_status === "Completed" ? <button type="button" className="contract-print-package" disabled={retrying || !employee.source_documents?.length} onClick={() => retryGeneration(false)}>{retrying ? "Отправляем…" : "Повторно сформировать"}</button> : null}<button type="button" className="contract-print-package" disabled={!employee.documents.length || printing} onClick={printPackage}>{printing ? "Готовим PDF…" : "Распечатать весь пакет"}</button></div></div>
            {employee.documents.length ? <div className="contract-documents-grid">{employee.documents.map(document => <ContractCard key={document.id} document={document} onDelete={remove} onOpen={openDocument} opening={openingDocumentId === document.id} />)}</div> : <div className="contract-empty"><span aria-hidden="true">□</span><div><strong>Договоров пока нет</strong><p>Дождитесь автоматического составления или загрузите готовый файл ниже.</p></div></div>}
        </section>
        <section className="contract-manual-section">
            <div className="contract-manual-heading"><span aria-hidden="true">+</span><div><strong>Добавить готовый документ</strong><small>Если договор подготовлен отдельно, прикрепите его к карточке сотрудника</small></div></div>
            <form className="contract-upload-form" onSubmit={upload}>
                <select aria-label={`Тип договора для ${employee.full_name}`} value={type} onChange={event => setType(event.target.value)}>{Object.entries(CONTRACT_TYPES).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
                <label className={`contract-file-picker ${file ? "has-file" : ""}`}><input aria-label={`Файл договора для ${employee.full_name}`} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={event => setFile(event.target.files?.[0] || null)} /><span>{file?.name || "Выбрать JPG, PNG, WEBP или PDF"}</span></label>
                <button disabled={!file || uploading}>{uploading ? "Загрузка…" : "Загрузить"}</button>
            </form>
        </section>
        <footer className="employee-contract-footer"><p>Нужно исправить исходный пакет?</p><button type="button" disabled={returning} onClick={returnToCandidateDocuments}>{returning ? "Возвращаем…" : "Вернуть в документы кандидатов"}</button></footer>
    </section>;
}

export default function EmployeeContractsPage() {
    const { showAlert } = useAlertContext();
    const [query, setQuery] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [manualOpen, setManualOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setAppliedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const load = useCallback(async () => {
        setLoading(true);
        try { setEmployees(await listEmployeeContracts(appliedQuery)); }
        catch (error) { showAlert(error.message || "Не удалось получить договоры", "error"); }
        finally { setLoading(false); }
    }, [appliedQuery, showAlert]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => ({
        all: employees.length,
        ready: employees.filter(employee => employeeMatchesFilter(employee, "ready")).length,
        processing: employees.filter(employee => employeeMatchesFilter(employee, "processing")).length,
        issues: employees.filter(employee => employeeMatchesFilter(employee, "issues")).length,
    }), [employees]);
    const visibleEmployees = useMemo(
        () => employees.filter(employee => employeeMatchesFilter(employee, filter)),
        [employees, filter],
    );
    const totalDocuments = employees.reduce((total, employee) => total + (employee.documents?.length || 0), 0);

    return <MainLayout>
        <div className="contracts-page">
            <header className="contracts-page-heading"><div><span>РАБОТА С ДОКУМЕНТАМИ</span><h1>Договоры кандидатов</h1><p>Проверяйте автоматически сформированные договоры, работайте с исходниками и добавляйте готовые файлы.</p></div><div className="contracts-page-heading-actions"><button type="button" onClick={() => setManualOpen(value => !value)}>{manualOpen ? "Закрыть форму" : "+ Создать пакет вручную"}</button><a href="/candidate-documents">← Документы кандидатов</a></div></header>
            {manualOpen ? <ManualGenerationForm onCancel={() => setManualOpen(false)} onCreated={async () => { setManualOpen(false); await load(); }} /> : null}
            <section className="contracts-overview" aria-label="Сводка по договорам">
                <div><span className="is-employees" aria-hidden="true">С</span><p>Сотрудники<strong>{employees.length}</strong></p></div>
                <div><span className="is-documents" aria-hidden="true">Д</span><p>Готовые документы<strong>{totalDocuments}</strong></p></div>
                <div><span className="is-processing" aria-hidden="true">↻</span><p>В обработке<strong>{counts.processing}</strong></p></div>
                <div><span className="is-issues" aria-hidden="true">!</span><p>Требуют внимания<strong>{counts.issues}</strong></p></div>
            </section>
            <div className="contracts-toolbar">
                <label className="contracts-search"><span aria-hidden="true">⌕</span><input aria-label="Поиск сотрудника" value={query} onChange={event => setQuery(event.target.value)} placeholder="ФИО, должность, подразделение или телефон" />{query ? <button type="button" onClick={() => setQuery("")} aria-label="Очистить поиск">×</button> : null}</label>
                <div className="contract-filter-tabs" role="group" aria-label="Фильтр договоров">{CONTRACT_FILTERS.map(item => <button type="button" key={item.value} className={filter === item.value ? "is-active" : ""} onClick={() => setFilter(item.value)}>{item.label}<span>{counts[item.value]}</span></button>)}</div>
            </div>
            {loading ? <div className="contracts-state is-loading"><span/><p>Загружаем договоры…</p></div> : null}
            {!loading && !employees.length ? <div className="contracts-state"><strong>Сотрудники не найдены</strong><p>Сотрудник появится здесь после трудоустройства и подтверждения полного пакета документов.</p></div> : null}
            {!loading && employees.length > 0 && !visibleEmployees.length ? <div className="contracts-state"><strong>В этой категории пока пусто</strong><p>Выберите другой фильтр, чтобы увидеть остальные договоры.</p></div> : null}
            <div className="employee-contract-list">{visibleEmployees.map(employee => <EmployeeContracts key={employee.id} employee={employee} onChanged={load} />)}</div>
        </div>
    </MainLayout>;
}
